import * as React from 'react';
import { createPortal } from 'react-dom';
import { createPopper } from '@popperjs/core';
import { Transition } from 'react-transition-group';
import { tabbable } from 'tabbable';
import Badge from '@telia-front/badge';
import Button from '@telia-front/button';
import Card, { CardContent } from '@telia-front/card';
import ChoiceGroup from '@telia-front/choice-group';
import { enableBodyScroll, disableBodyScroll, useIsMounted, usePrevious } from '@telia-front/helper';
import Icon from '@telia-front/icon';
import { useLabels } from '@telia-front/label-provider';
import Skeleton, { SkeletonItem } from '@telia-front/skeleton';
import { Slider } from '@telia-front/slider';
import { useTheme } from '@telia-front/theme-provider';
const HorizontalFilterItem = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
    let popper = null;
    let button = null;
    let dropdown = null;
    let closeButton = null;
    const choiceGroupValueProp = typeof ((_b = (_a = props.inputType) === null || _a === void 0 ? void 0 : _a.choiceGroup) === null || _b === void 0 ? void 0 : _b.value) === 'string' ? [(_d = (_c = props.inputType) === null || _c === void 0 ? void 0 : _c.choiceGroup) === null || _d === void 0 ? void 0 : _d.value] : (_f = (_e = props.inputType) === null || _e === void 0 ? void 0 : _e.choiceGroup) === null || _f === void 0 ? void 0 : _f.value;
    const choiceGroupDefaultValue = typeof ((_h = (_g = props.inputType) === null || _g === void 0 ? void 0 : _g.choiceGroup) === null || _h === void 0 ? void 0 : _h.defaultValue) === 'string' ? [(_k = (_j = props.inputType) === null || _j === void 0 ? void 0 : _j.choiceGroup) === null || _k === void 0 ? void 0 : _k.defaultValue] : (_m = (_l = props.inputType) === null || _l === void 0 ? void 0 : _l.choiceGroup) === null || _m === void 0 ? void 0 : _m.defaultValue;
    const choiceGroupValue = choiceGroupValueProp || choiceGroupDefaultValue;
    const defaultIsActiveValue = ((_p = (_o = props.inputType) === null || _o === void 0 ? void 0 : _o.slider) === null || _p === void 0 ? void 0 : _p.defaultValue) || !!(choiceGroupValue === null || choiceGroupValue === void 0 ? void 0 : choiceGroupValue.length) || !!((_r = (_q = props.inputType) === null || _q === void 0 ? void 0 : _q.switch) === null || _r === void 0 ? void 0 : _r.defaultValue);
    const defaultSliderValue = !!((_t = (_s = props.inputType) === null || _s === void 0 ? void 0 : _s.slider) === null || _t === void 0 ? void 0 : _t.defaultValue);
    const isSwitch = !(!!((_u = props.inputType) === null || _u === void 0 ? void 0 : _u.choiceGroup) || !!((_v = props.inputType) === null || _v === void 0 ? void 0 : _v.slider));
    const labels = useLabels();
    const [isOpen, setIsOpen] = React.useState(false);
    const [isActive, setIsActive] = React.useState(defaultIsActiveValue);
    const [showSliderValue, setShowSliderValue] = React.useState(defaultSliderValue);
    const [popperStyle, setPopperStyle] = React.useState();
    const prevState = usePrevious(isOpen);
    const prevProps = usePrevious(props.isOpen);
    const isMounted = useIsMounted();
    const showClearButton = !!(choiceGroupValue === null || choiceGroupValue === void 0 ? void 0 : choiceGroupValue.length) || showSliderValue || (isSwitch && isActive);
    React.useEffect(() => {
        document.addEventListener('mousedown', onClickOutside);
        if ((props.isOpen && !prevProps) || (isOpen && !prevState)) {
            handleOpen();
        }
        else if ((!props.isOpen && prevProps) || (!isOpen && prevState)) {
            handleClose();
        }
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
        };
    });
    React.useEffect(() => {
        if (!isSwitch && (isOpen || props.isOpen)) {
            setIsActive(true);
        }
        else if (!isSwitch && (!isOpen || !props.isOpen)) {
            setIsActive(!!(choiceGroupValue === null || choiceGroupValue === void 0 ? void 0 : choiceGroupValue.length) || showSliderValue);
        }
    }, [choiceGroupValue === null || choiceGroupValue === void 0 ? void 0 : choiceGroupValue.length, isSwitch, isOpen, showSliderValue, props.isOpen]);
    React.useEffect(() => {
        setIsOpen(!!props.isOpen);
    }, [props.isOpen]);
    const isOpenControlled = (isOpen = props.isOpen) => {
        return !!props.onToggle && typeof isOpen !== 'undefined';
    };
    const getIsOpen = () => {
        return isOpenControlled(props.isOpen) ? props.isOpen : isOpen;
    };
    const enablePopper = () => {
        initPopper();
    };
    const disablePopper = () => {
        if (popper) {
            popper.destroy();
            popper = null;
        }
    };
    const initPopper = () => {
        if (getIsOpen() && dropdown && button && !popper) {
            popper = createPopper(button, dropdown, {
                modifiers: [
                    { name: 'applyStyles', enabled: false },
                    { name: 'flip', enabled: false },
                    {
                        name: 'updateState',
                        enabled: true,
                        phase: 'write',
                        requires: ['computeStyles'],
                        fn: ({ state }) => {
                            setPopperStyle(Object.assign({}, state.styles.popper));
                        },
                    },
                ],
                placement: 'bottom-start',
            });
        }
    };
    const close = () => {
        setIsOpen(false);
        button === null || button === void 0 ? void 0 : button.focus();
        if (props.onClose && getIsOpen()) {
            props.onClose();
        }
        if (props.onToggle && getIsOpen() && !isSwitch) {
            props.onToggle(false);
        }
    };
    const dropdownBEM = (state, scrollable) => {
        const classArray = ['horizontal-filter-item__dropdown', `is-${state}`];
        if (props.className) {
            classArray.push(props.className);
        }
        if (scrollable) {
            classArray.push('is-scrollable');
        }
        return classArray.join(' ');
    };
    const handleOpen = () => {
        enablePopper();
    };
    const handleClose = () => {
        disablePopper();
    };
    const onClickOutside = (event) => {
        const clickedOnTrigger = button && event.target instanceof Element && button.contains(event.target);
        const clickedOnDropdown = dropdown && event.target instanceof Node && dropdown.contains(event.target);
        if (clickedOnTrigger || clickedOnDropdown) {
            return;
        }
        if (getIsOpen()) {
            close();
        }
    };
    const handleFilterButtonClick = (event) => {
        var _a, _b;
        event.preventDefault();
        const clickedOnCloseButton = closeButton && event.target instanceof Element && closeButton.contains(event.target);
        if (clickedOnCloseButton) {
            return;
        }
        setIsOpen(!isOpen);
        if (props.onClose && getIsOpen()) {
            props.onClose();
        }
        if (props.onToggle) {
            props.onToggle(!getIsOpen());
        }
        if ((_b = (_a = props.inputType) === null || _a === void 0 ? void 0 : _a.slider) === null || _b === void 0 ? void 0 : _b.start) {
            setShowSliderValue(true);
        }
    };
    const setFocus = () => {
        if (dropdown && isOpen) {
            const firstTabbableElement = tabbable(dropdown)[0];
            if (firstTabbableElement) {
                disableBodyScroll();
                firstTabbableElement.focus();
                enableBodyScroll();
                return;
            }
        }
    };
    const handleSwitchClick = (event) => {
        event.preventDefault();
        setIsActive(!isActive);
        if (props.onClose && getIsOpen()) {
            props.onClose();
        }
        if (props.onToggle) {
            props.onToggle(!isActive);
        }
    };
    const onKeyDown = (event) => {
        switch (event.key) {
            case 'Escape':
            case 'Esc':
                if (getIsOpen()) {
                    event.preventDefault();
                    close();
                }
                return;
            case event.shiftKey && 'Tab':
                setIsOpen(false);
        }
    };
    const renderDropdown = () => {
        var _a;
        const dropdownContent = (_a = document.getElementById('horizontal-filter__dropdown')) === null || _a === void 0 ? void 0 : _a.children[0].children[0];
        const dropdownContentChild = dropdownContent && dropdownContent.children[0];
        const hasVerticalScrollbar = dropdownContentChild && dropdownContent.scrollHeight > dropdownContentChild.clientHeight;
        const content = (state) => {
            var _a, _b, _c, _d;
            return (React.createElement("div", { ref: (el) => (dropdown = el), className: dropdownBEM(state, hasVerticalScrollbar), style: popperStyle, "aria-hidden": true, id: "horizontal-filter__dropdown" },
                React.createElement(Card, { elevation: 5, background: "neutral-bg-level-3", className: "horizontal-filter-item__dropdown-inner" },
                    React.createElement(CardContent, { padding: {
                            bottom: hasVerticalScrollbar ? 'medium' : 'none',
                        } },
                        ((_a = props.inputType) === null || _a === void 0 ? void 0 : _a.choiceGroup) && React.createElement(ChoiceGroup, Object.assign({}, props.inputType.choiceGroup)),
                        ((_b = props.inputType) === null || _b === void 0 ? void 0 : _b.slider) && React.createElement(Slider, Object.assign({}, props.inputType.slider))),
                    React.createElement(CardContent, { className: "horizontal-filter-item__dropdown-footer", background: hasVerticalScrollbar ? 'neutral-bg-level-4' : 'neutral-bg-level-3' },
                        React.createElement(Button, Object.assign({ size: "small" }, props.saveButton, { text: ((_c = props.saveButton) === null || _c === void 0 ? void 0 : _c.text) || labels.get('horizontalFilter.apply'), onClick: e => {
                                var _a;
                                ((_a = props.saveButton) === null || _a === void 0 ? void 0 : _a.onClick) && props.saveButton.onClick(e);
                                close();
                            } })),
                        React.createElement(Button, Object.assign({ link: true }, props.resetButton, { text: ((_d = props.resetButton) === null || _d === void 0 ? void 0 : _d.text) || labels.get('horizontalFilter.reset'), onClick: e => {
                                var _a;
                                ((_a = props.resetButton) === null || _a === void 0 ? void 0 : _a.onClick) && props.resetButton.onClick(e);
                                close();
                                setShowSliderValue(defaultSliderValue);
                            }, onKeyDown: e => {
                                e.key === 'Tab' ? close() : undefined;
                            } }))))));
        };
        return (React.createElement(Transition, { in: getIsOpen(), timeout: { enter: 10, exit: 100 }, mountOnEnter: true, unmountOnExit: true, onExited: disablePopper, onEntered: setFocus }, content));
    };
    const BEM = () => {
        const classArray = ['horizontal-filter-item'];
        if (isOpen) {
            classArray.push('is-open');
        }
        if (isActive) {
            classArray.push('is-active');
        }
        if (showClearButton) {
            classArray.push('is-clearable');
        }
        return classArray.join(' ');
    };
    const getSliderValues = (slider) => {
        var _a, _b;
        const values = (_b = (_a = props.inputType) === null || _a === void 0 ? void 0 : _a.slider) === null || _b === void 0 ? void 0 : _b.start;
        if (values) {
            if (values[0] === values[1]) {
                return `: ${values[0]}${slider.unit}`;
            }
            return `: ${values[0]}-${values[1]}${slider.unit}`;
        }
    };
    const onClearButtonClick = (event) => {
        event.preventDefault();
        if (isSwitch) {
            setIsActive(false);
            return;
        }
        props.clearButtonOnClick && props.clearButtonOnClick(event);
        close();
        setShowSliderValue(false);
    };
    const renderClearButton = () => {
        return (React.createElement(Button, { id: `${props.id}-close`, onClick: onClearButtonClick, ref: el => (closeButton = el), icon: "#close-round", link: true, text: labels.get('clear'), className: "horizontal-filter-item__clear-btn", "aria-labelledby": `${props.id}-close ${props.id}` }));
    };
    const renderButton = () => {
        var _a;
        if (isSwitch) {
            return (React.createElement("div", { className: "horizontal-filter-item__wrapper" },
                React.createElement("button", { className: BEM(), onClick: handleSwitchClick, type: "button", id: props.id }, props.label),
                showClearButton && renderClearButton()));
        }
        else {
            return (React.createElement("div", { className: "horizontal-filter-item__wrapper" },
                React.createElement("button", { className: BEM(), onClick: handleFilterButtonClick, ref: el => (button = el), "aria-expanded": getIsOpen(), "aria-controls": props.id, onKeyDown: onKeyDown, type: "button", id: props.id },
                    props.label,
                    (choiceGroupValue === null || choiceGroupValue === void 0 ? void 0 : choiceGroupValue.length) ? React.createElement(Badge, { text: choiceGroupValue.length, size: "small" }) : undefined,
                    ((_a = props.inputType) === null || _a === void 0 ? void 0 : _a.slider) && showSliderValue && getSliderValues(props.inputType.slider),
                    React.createElement(Icon, { modifiers: "#arrow-down", size: "xs", className: "horizontal-filter-item__icon" })),
                showClearButton && renderClearButton()));
        }
    };
    const renderSkeleton = (props) => {
        const { skeleton } = props;
        return (React.createElement(Skeleton, Object.assign({}, skeleton),
            React.createElement(SkeletonItem, { width: "128px", height: "48px" })));
    };
    const { portalRoot } = useTheme();
    const renderHorizontalFilterItem = () => {
        if ('skeleton' in props) {
            return renderSkeleton(props);
        }
        else {
            return (React.createElement(React.Fragment, null,
                renderButton(),
                !isSwitch && isMounted && createPortal(renderDropdown(), props.portalContainer || portalRoot || document.body)));
        }
    };
    return renderHorizontalFilterItem();
};
export default HorizontalFilterItem;
